import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import axiosInstance from "axios.js";
import { useToastify } from "app/hooks/useToastify";

type ResgateCupomResponse = {
  success?: boolean;
  message?: string;
  [key: string]: unknown;
};

type RouteParams = {
  rc?: string;
};

const ResgateCupomPage: React.FC = () => {
  const toast = useToastify();
  const location = useLocation();
  const routeParams = useParams<RouteParams>();

  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [responseData, setResponseData] = useState<ResgateCupomResponse | null>(null);
  const [codPedido, setCodPedido] = useState<string>("");
  const [isMobile, setIsMobile] = useState<boolean>(false);

  const fetchCupom = useCallback(async (pedido: string) => {
    setLoading(true);
    setResponseMessage(null);
    setErrorMessage(null);
    setResponseData(null);

    try {
      const endpoint = `/anunciantes/validaterc/${encodeURIComponent(pedido)}`;
      const response = await axiosInstance.get<ResgateCupomResponse>(endpoint);

      const data = response.data ?? {};
      setResponseData(data);

      const fallbackSuccessMessage = "Cupom resgatado com sucesso";
      const fallbackErrorMessage = "Não foi possível resgatar o cupom. Verifique o código informado.";
      const apiMessage = data.message;

      if (response.status >= 200 && response.status < 300 && data.success) {
        const message = apiMessage ?? fallbackSuccessMessage;
        setResponseMessage(message);
        //toast.show("success", message);
        return;
      }

      const message = apiMessage ?? fallbackErrorMessage;
      setErrorMessage(message);
      //toast.show("error", message);
    } catch (error) {
      let message = "Não foi possível conectar ao serviço. Tente novamente em instantes.";

      setErrorMessage(message);
      toast.show("error", message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pedidoFromQuery = params.get("codPedido")?.trim() ?? "";
    const pedidoFromRoute = routeParams.rc?.trim() ?? "";
    const pedido = pedidoFromQuery || pedidoFromRoute;

    setCodPedido(pedido);

    if (!pedido) {
      setLoading(false);
      setResponseMessage(null);
      setResponseData(null);
      setErrorMessage("Código do pedido não informado.");
      return;
    }

    fetchCupom(pedido);
  }, [fetchCupom, location.search, routeParams.rc]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 640px)");
    const updateMatch = () => setIsMobile(mediaQuery.matches);

    updateMatch();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateMatch);
      return () => mediaQuery.removeEventListener("change", updateMatch);
    }

    mediaQuery.addListener(updateMatch);
    return () => mediaQuery.removeListener(updateMatch);
  }, []);

  const desktopBackgroundUrl = `${process.env.PUBLIC_URL}/assets/images/usemais/bg_pagina_parceiro.png`;
  const mobileBackgroundUrl = `${process.env.PUBLIC_URL}/assets/images/usemais/bg_pagina_parceiro_mobile.png`;
  const backgroundImageUrl = isMobile ? mobileBackgroundUrl : desktopBackgroundUrl;

  return (
    <div
      className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-bg-gray-50 tw-p-6"
      style={{
        backgroundImage: `url(${backgroundImageUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="tw-w-full tw-max-w-lg tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-px-8 tw-py-10 tw-shadow-sm">
        <h1 className="tw-mb-6 tw-text-center tw-text-2xl tw-font-semibold tw-text-gray-900">
          Validação de Benefício
        </h1>

        <div className="tw-space-y-4">
          <div className="tw-rounded-lg tw-border tw-border-gray-100 tw-bg-gray-50 tw-p-4">
            <p className="tw-text-sm tw-font-medium tw-text-gray-600">
              Código informado:{" "}
              <span className="tw-font-semibold tw-text-gray-900">
                {codPedido || "Não informado"}
              </span>
            </p>
          </div>

          {loading && (
            <p className="tw-text-center tw-text-sm tw-font-medium tw-text-gray-600">
              Consultando dados do cupom...
            </p>
          )}

          {responseMessage && (
            <div className="tw-rounded-lg tw-border tw-border-green-200 tw-bg-green-50 tw-p-4">
              <p className="tw-text-center tw-text-sm tw-font-semibold tw-text-green-700">{responseMessage}</p>
            </div>
          )}

        </div>

      </div>
    </div>
  );
};

export default ResgateCupomPage;
